import React, { useState, useContext, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { UserDeletePrompt } from "../utils/UserDeletePrompt";
import { UsersManagementProps } from "../../../../pages/v2/UsersPage";
import { Role, SubRoles, _AuthType } from "../../../../enums";
import { DataGrid, GridColDef, GridColumns } from "@mui/x-data-grid";
import { ThemeContext } from "../../../../context/ThemeContext";
import { ThemeCtx } from "../../../../interfaces";
import { ViewUser } from "../ViewDetails/ViewUser";
import { ShareAccessModal } from "../ShareAccessModal";
import {
  AccessListItem,
  addObservers,
  ClassListItem,
  deleteObserver,
  generateGtmEvent,
  getObservers,
  ReSendLoginCredentialsToUser,
} from "../../../../Helpers";
import { ErrorToast, SuccessToast } from "../../utils/Toast";
import { Stack, Tooltip } from "@mui/material";
import axios from "../../../../services/axios";
import { PopUpDialog } from "../../utils/PopUpDialog";
import Typography from "@mui/material/Typography";
import { SettingsButton } from "../utils/SettingsButton";
import { MacsAuthenticator } from "../../../../hooks/useMacsAuthenticator";
import { ViewUserManagement } from "@backend-types";
import { EditUserV2 } from "../EditDetails/EditUserV2";

const useStyles = makeStyles((theme) => ({
  root: {
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
  },
  dataTable: {
    borderRadius: "8px",
    backgroundColor: "white",
    borderColor: "#E8E8E8",
    "& .MuiDataGrid-iconButtonContainer": {
      marginLeft: "auto",
    },
    "& .MuiDataGrid-columnHeaders": {
      height: "50px",
      minHeight: "unset",
    },
    "& .MuiDataGrid-columnHeader": {
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    "& .MuiDataGrid-columnHeader--sorted": {
      color: theme.palette.primary.main,
      "& .MuiDataGrid-sortIcon": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiDataGrid-cell": {
      border: "0px",
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
  },
  dataTableHeader: {
    "& > .MuiDataGrid-columnSeparator": {
      visibility: "hidden",
    },
  },
  usersTable: {
    "& thead": {
      color: "#FFF",
      backgroundColor: theme.palette.primary.main,
    },
    "& th": {
      padding: "0.625rem",
    },
    "& td": {
      padding: "0.625rem",
    },
  },
  evenRow: {
    backgroundColor: "#FFF",
  },
  oddRow: {
    backgroundColor: "#F5F5F5",
  },
  deleteUserButton: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: 600,
    borderRadius: "2.25rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

export const UsersTable: React.FunctionComponent<UsersManagementProps> = (props) => {
  const { heading = "Your users" } = props;
  const classes = useStyles();
  const [promptOpen, setPromptOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState<ViewUserManagement | undefined>();
  const [viewOpen, setViewOpen] = useState(false);
  const [viewUser, setViewUser] = useState<ViewUserManagement>();
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<ViewUserManagement>();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUser, setShareUser] = useState<ViewUserManagement>();
  const [observerList, setObserverList] = useState<AccessListItem[]>([]);
  const [studentClassList, setStudentClassList] = useState<ClassListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { listContainsDisabledItems = false } = props;
  const [reasonPromptOpen, setReasonPromptOpen] = useState(false);
  const [disabledReason] = useState("OTHER");
  const [disabledUser, setDisabledUser] = useState<ViewUserManagement>();
  const [loginCredentialsPromptOpen, setLoginCredentialsPromptOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ViewUserManagement>();
  const [sendLoginCredentialsError, setSendLoginCredentialsError] = useState<
    undefined | string
  >(undefined);

  let display = props.currentUser?.role === Role.super;
  const themeCtx = useContext(ThemeContext) as ThemeCtx;

  const handlePromptClose = () => {
    setPromptOpen(false);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleViewClose = () => {
    setViewOpen(false);
  };

  const handleUserDeleteClick = (user: ViewUserManagement) => {
    setDeleteUser(user);
    setPromptOpen(true);
  };

  const handleUserEditClick = (user: ViewUserManagement) => {
    setEditUser(user);
    setViewOpen(false);
    setEditOpen(true);
  };

  const handleUserViewClick = (user: ViewUserManagement) => {
    setViewUser(user);
    setViewOpen(true);
  };

  const handleShareUserClick = (user: ViewUserManagement) => {
    setShareOpen(true);
    setShareUser(user);
  };

  useEffect(() => {
    const getStudentObserver = async (shareUser: any) => {
      let result = await getObservers(`studentId=${shareUser?.id}`);
      setObserverList([
        // ...[
        //   {
        //     id: shareUser.teacher_id,
        //     full_name: shareUser.teacher_name,
        //     email: shareUser.teacher_email,
        //     role: "Teacher",
        //   },
        // ],
        ...result.teachers,
        ...result.observers,
      ]);
      setStudentClassList(result.studentsClasses);
      setIsLoading(false);
    };
    if (shareOpen && shareUser) {
      setIsLoading(true);
      getStudentObserver(shareUser);
    }
  }, [shareOpen, shareUser]);

  const renderCell = (params: any) => {
    const isPiiField = params.field === "email";
    return (
      <div
        {...(isPiiField ? { "data-sentry-mask": true } : {})}
        style={{
          fontWeight: props.sort?.field === params.field ? 500 : "inherit",
          color:
            props.sort?.field === params.field
              ? themeCtx.theme.palette.primary.main
              : "inherit",
        }}>
        {params.value}
      </div>
    );
  };

  const renderCellDate = (params: any) => {
    return (
      <div
        style={{
          fontWeight: props.sort?.field === params.field ? 500 : "inherit",
          color:
            props.sort?.field === params.field
              ? themeCtx.theme.palette.primary.main
              : "inherit",
        }}>
        {params.value
          ? new Date(params.value).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })
          : ""}
      </div>
    );
  };

  const toggleUserAccess = async (
    user: ViewUserManagement,
    toDisable: boolean,
    reason?: string
  ) => {
    try {
      const response = await axios.post("/user/access", {
        id: user.id,
        toDisable,
        reason,
      });
      if (response.status === 200) {
        SuccessToast(
          `${!toDisable ? "Activated" : "Deactivated"} ${user.email} successfully`
        );
        props.onDelete(null);
      }
    } catch (e: any) {
      if (e) {
        ErrorToast(e.response.data.message);
      }
    }
  };
  const handleUserActionClick = async (user: ViewUserManagement) => {
    if (!user.is_active) {
      // user already disabled so enable
      toggleUserAccess(user, user.is_active);
    } else {
      // user is enabled popup reason box and disable
      setDisabledUser(user);
      setReasonPromptOpen(true);
    }
  };
  const handleSendUserLoginCredentials = async (user: ViewUserManagement) => {
    setSelectedUser(user);
    setLoginCredentialsPromptOpen(true);
  };

  useEffect(() => {
    if (!loginCredentialsPromptOpen) {
      setSendLoginCredentialsError(undefined);
    }
  }, [loginCredentialsPromptOpen]);

  const fieldMap = new Map<string, GridColDef<ViewUserManagement>>([
    [
      "observer_ids",
      {
        field: "observer_ids",
        headerName: "",
        maxWidth: 20,
        headerClassName: classes.dataTableHeader,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => {
          if (params.value && params.value.includes(props.currentUser.id)) {
            let toolTipLabel = "";
            if (params.row.class_observer_ids?.includes(props.currentUser.id)) {
              toolTipLabel = "You're observing this student's class";
            } else if (params.row.student_observer_ids?.includes(props.currentUser.id)) {
              toolTipLabel = "You're observing this student";
            }
            return (
              <Tooltip title={toolTipLabel} placement="bottom" arrow>
                <i
                  className="far fa-eye fa-lg"
                  style={{ color: themeCtx.theme.palette.text.secondary }}></i>
              </Tooltip>
            );
          } else {
            return <></>;
          }
        },
      },
    ],
    [
      "first_name",
      {
        field: "first_name",
        headerName: "First Name",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              data-sentry-mask
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {params.row.first_name}
            </div>
          );
        },
      },
    ],
    [
      "last_name",
      {
        field: "last_name",
        headerName: "Last Name",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              data-sentry-mask
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {params.row.last_name}
            </div>
          );
        },
      },
    ],
    [
      "full_name",
      {
        field: "full_name",
        headerName: "Name",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              data-sentry-mask
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {params.row.full_name}
            </div>
          );
        },
      },
    ],
    [
      "email",
      {
        field: "email",
        headerName: "Email",
        flex: 2,
        headerClassName: classes.dataTableHeader,
        renderCell,
      },
    ],
    [
      "role",
      {
        field: "role",
        headerName: "Role",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell,
      },
    ],
    [
      "school_name",
      {
        field: "school_name",
        headerName: "School",
        flex: 2,
        headerClassName: classes.dataTableHeader,
        renderCell,
      },
    ],
    [
      "subject_period_combined",
      {
        field: "subject_period_combined",
        headerName: "Class",
        flex: 2,
        headerClassName: classes.dataTableHeader,
        renderCell,
      },
    ],
    [
      "created_at",
      {
        field: "created_at",
        headerName: "Created",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: renderCellDate,
      },
    ],
    [
      "last_access_at",
      {
        field: "last_access_at",
        headerName: "Last log in",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: renderCellDate,
      },
    ],
    [
      "is_active",
      {
        field: "is_active",
        headerName: "Status",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: (params: any) => {
          return (
            <div
              style={{
                fontWeight: props.sort?.field === params.field ? 500 : "inherit",
              }}>
              {!params.value ? (
                <i
                  className="fa-regular fa-circle-minus fa-lg"
                  style={{ color: themeCtx.theme.palette.error.main }}></i>
              ) : (
                <i
                  className="fas fa-check fa-lg"
                  style={{
                    color: themeCtx.theme.palette.success.main,
                  }}></i>
              )}
              {!params.value ? " Disabled" : " Active"}
            </div>
          );
        },
      },
    ],
    [
      "disabled_by",
      {
        field: "disabled_by",
        headerName: "Disabled By",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell,
      },
    ],
    [
      "disabled_at",
      {
        field: "disabled_at",
        headerName: "Disabled At",
        flex: 1,
        headerClassName: classes.dataTableHeader,
        renderCell: renderCellDate,
      },
    ],
    [
      "action",
      {
        field: "action",
        headerName: "Actions",
        headerAlign: "right",
        headerClassName: classes.dataTableHeader,
        align: "right",
        minWidth: 55,
        sortable: false,
        renderCell: (params) => {
          const isReadOnlyAdmin = props.currentUser?.user_subrole?.some(
            (role) => role.sub_role === SubRoles.ADMIN_READ_ONLY
          );
          const actions = [
            {
              label: "Edit",
              color: "#747474",
              onClick: () => handleUserEditClick(params.row),
              visible: !isReadOnlyAdmin,
              isDisabled: false,
            },
            {
              label: "View",
              color: "#747474",
              onClick: () => handleUserViewClick(params.row),
              visible: true,
              isDisabled: false,
            },
            {
              label: "Delete",
              color: "#CC2F2F",
              onClick: () => handleUserDeleteClick(params.row),
              visible: false,
              isDisabled: false,
            },
            {
              label: "Share",
              color: "#747474",
              onClick: () => handleShareUserClick(params.row),
              isDisabled:
                params.row.class_observer_ids?.includes(props.currentUser.id) ||
                params.row.student_observer_ids?.includes(props.currentUser.id) ||
                !params.row.is_active,
              visible: !isReadOnlyAdmin && params.row.role === Role.student,
            },
            {
              label: "Send login credentials",
              color: "#747474",
              onClick: () => handleSendUserLoginCredentials(params.row),
              isDisabled: false,
              visible:
                !isReadOnlyAdmin && MacsAuthenticator.authHook.authType !== _AuthType.LTI,
            },
            {
              label: "Activate account",
              color: "#747474",
              onClick: () => handleUserActionClick(params.row),
              visible:
                (props.currentUser?.role === Role.super ||
                  (props.currentUser?.role === Role.admin && !isReadOnlyAdmin)) &&
                !params.row.is_active,
              isDisabled: false,
            },
            {
              label: "Deactivate account",
              color: "#CC2F2F",
              onClick: () => handleUserActionClick(params.row),
              visible:
                (props.currentUser?.role === Role.super ||
                  (props.currentUser?.role === Role.admin && !isReadOnlyAdmin)) &&
                params.row.is_active,
              isDisabled: false,
            },
          ];
          return (
            <SettingsButton
              gtmComponent={heading}
              sectionName={params.row.full_name ?? ""}
              actions={actions}
            />
          );
        },
      },
    ],
  ]);

  const getField = (fieldName: string): GridColDef<ViewUserManagement> | void => {
    const field = fieldMap.get(fieldName);
    return field;
  };

  const buildColumnDef = (): GridColumns<ViewUserManagement> => {
    const columns: GridColumns<ViewUserManagement> = [];

    if (!props.columnNames) {
      columns.push(
        ...[
          getField("observer_ids")!,
          ...(!listContainsDisabledItems
            ? [getField("first_name")!, getField("last_name")!]
            : []),
          ...(listContainsDisabledItems ? [getField("full_name")!] : []),
          ...(display ? [getField("email")!] : []),
          getField("role")!,
          getField("school_name")!,
          ...(!display ? [getField("subject_period_combined")!] : []),
          ...(!listContainsDisabledItems
            ? [getField("created_at")!, getField("last_access_at")!]
            : []),
          ...(listContainsDisabledItems
            ? [
                getField("is_active")!,
                getField("disabled_by")!,
                // {
                //   field: "disabled_reason",
                //   headerName: "Reason",
                //   flex: 1.2,
                //   headerClassName: classes.dataTableHeader,
                //   renderCell: (params: any) => {
                //     return (
                //       <Tooltip
                //         title={DISABLED_USER_REASON[params.value]}
                //         placement={"bottom"}
                //         arrow>
                //         <div
                //           style={{
                //             cursor: "pointer",
                //             fontWeight:
                //               props.sort?.field === params.field ? 500 : "inherit",
                //           }}>
                //           {DISABLED_USER_REASON[params.value]}
                //         </div>
                //       </Tooltip>
                //     );
                //   },
                // },
                getField("disabled_at")!,
              ]
            : []),
          getField("action")!,
        ]
      );
    } else {
      props.columnNames.forEach((fieldName) => {
        columns.push(getField(fieldName)!);
      });
    }
    return columns;
  };

  const dataGridColumns = buildColumnDef();

  return (
    <>
      <div className={classes.root}>
        <div style={{ width: "100%" }}>
          <DataGrid
            className={classes.dataTable}
            rows={props.users || []}
            columns={dataGridColumns}
            sortingMode="server"
            onSortModelChange={(model) => {
              try {
                const colDef = dataGridColumns.find(
                  (item) => item.field === model[0].field
                );

                const headerName = colDef?.headerName;
                generateGtmEvent({
                  event: "link",
                  action: "click",
                  name: "onclick",
                  type: "internal link",
                  region: "main content",
                  section: `${heading}`,
                  text: `${headerName ?? model[0].field} ^ sort by`,
                });
                props.sort?.handleSort(model);
              } catch (e) {
                console.log(props.sort);
                console.error(e, model);
              }
            }}
            initialState={{
              sorting: {
                sortModel: [
                  {
                    field: props.sort?.field || "first_name",
                    sort: props.sort?.sort || "asc",
                  },
                ],
              },
            }}
            autoHeight
            disableSelectionOnClick
            disableColumnSelector
            disableDensitySelector
            disableColumnMenu
            hideFooter
          />
        </div>
        <UserDeletePrompt
          promptOpen={promptOpen}
          promptOnClose={handlePromptClose}
          currentUser={props.currentUser}
          user={deleteUser}
          onDelete={props.onDelete}
        />
        {/** Edit User Modal when teacher or admin is assigned to multiple or no school we are not creating edit user modal because there's harcoded logic in this model which checks user should be exactly in 1 school and in some instances of LTI and SSO we are not getting school details for teachers */}
        {/* This breaks EditUser from ever opening as a SuperAdmin */}
        {/* {props.currentUser.user_school.length === 1 && (<EditUser
          open={editOpen}
          onClose={handleEditClose}
          onSubmit={props.onDelete}
          currentUser={props.currentUser}
          user={editUser}
        />)} */}
        {editUser && (
          <EditUserV2
            open={editOpen}
            onClose={handleEditClose}
            onSubmit={props.onDelete}
            currentUser={props.currentUser}
            user={editUser}
          />
        )}
        {viewUser && (
          <ViewUser
            open={viewOpen}
            onClose={handleViewClose}
            onEdit={() => handleUserEditClick(viewUser!)}
            currentUser={props.currentUser}
            user={viewUser}
          />
        )}
        <ShareAccessModal
          loading={isLoading}
          schoolId={`${shareUser?.school_id!}`}
          open={shareOpen}
          handleClose={() => {
            setShareOpen(false);
          }}
          assignAccess={async (observerList, selectedClassIds?: string[]) => {
            if (observerList.length > 0) {
              // await api call and send toast message
              try {
                const response = await addObservers(
                  {
                    studentId: shareUser?.id,
                    studentClassIds: selectedClassIds?.map((item) => Number(item)),
                    type: "OBSERVER_STUDENT",
                  },
                  observerList
                );
                if (response.some((res) => res.status === "fulfilled")) {
                  SuccessToast("Observer added successfully!");
                }
                if (response.some((res) => res.status === "rejected")) {
                  response.forEach((res) => {
                    if (res.status === "rejected") {
                      ErrorToast(res.reason);
                    }
                  });
                }
              } catch (e) {
                ErrorToast("Error adding observers");
              }
            }
            setObserverList([]);
          }}
          currentUser={props.currentUser}
          existingObservers={observerList}
          studentClassOptions={studentClassList}
          title={shareUser?.full_name ?? ""}
          accessFor={"student"}
          removeAccess={async (observerId) => {
            try {
              await deleteObserver({
                type: "OBSERVER_STUDENT",
                studentId: shareUser?.id,
                grantToId: observerId,
              });
              let newObserverList = observerList.filter(
                (observer) => observer.id !== observerId
              );
              setObserverList([...newObserverList]);
            } catch (e) {
              console.log(e);
            }
          }}
        />
        {disabledUser && (
          <PopUpDialog
            ctaFooterVariant={"default"}
            promptOpen={reasonPromptOpen}
            handleClose={() => {
              setReasonPromptOpen(false);
              generateGtmEvent({
                event: "modal",
                action: "close",
                name: "onclick",
                type: "click",
                region: "main content",
                section: "deactivate account",
                text: "close cross",
              });
            }}
            handleOkClick={() => {
              toggleUserAccess(disabledUser, disabledUser.is_active, disabledReason);
              setDisabledUser({} as ViewUserManagement);
              //setDisabledReason("");
              setReasonPromptOpen(false);
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "main content",
                section: "deactivate account",
                text: "deactivate user",
              });
            }}
            handleCancelClick={() => {
              setReasonPromptOpen(false);
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "main content",
                section: "deactivate account",
                text: "cancel",
              });
            }}
            okButtonLabel={"Deactivate user"}
            popupTitle={`Deactivate account`}
            cancelButtonLabel={"Cancel"}>
            <Typography variant="body1">
              Deactivating a user will remove their access to the tutor. Reactivate them
              at any time from the Archived page. Are you sure you want to deactivate{" "}
              {disabledUser.full_name}?
            </Typography>
            {/*<Typography variant="body1" sx={{ marginBottom: "24px" }}>*/}
            {/*  Please select a reason for disabling this user.*/}
            {/*</Typography>*/}
            {/*<FormControl variant="outlined" fullWidth>*/}
            {/*  <InputLabel id="disabled-reason-label">Reason</InputLabel>*/}
            {/*  <Select*/}
            {/*    labelId="disabled-reason-label"*/}
            {/*    id="disabled-reason"*/}
            {/*    value={disabledReason}*/}
            {/*    onChange={(e) => setDisabledReason(e.target.value as string)}*/}
            {/*    label="Reason">*/}
            {/*    {Object.keys(DISABLED_USER_REASON).map((key) => {*/}
            {/*      return <MenuItem value={key}>{DISABLED_USER_REASON[key]}</MenuItem>;*/}
            {/*    })}*/}
            {/*  </Select>*/}
            {/*</FormControl>*/}
          </PopUpDialog>
        )}
        {selectedUser && (
          <PopUpDialog
            ctaFooterVariant={sendLoginCredentialsError ? "default" : "stretched"}
            promptOpen={loginCredentialsPromptOpen}
            handleClose={() => {
              setLoginCredentialsPromptOpen(false);
              generateGtmEvent({
                event: "modal",
                action: "close",
                name: "onclick",
                type: "click",
                region: "main content",
                section: "send login credentials",
                text: "close cross",
              });
            }}
            handleOkClick={async () => {
              setLoginCredentialsPromptOpen(false);
              if (!sendLoginCredentialsError) {
                ReSendLoginCredentialsToUser(selectedUser.email)
                  .then(() => {
                    SuccessToast("Login credentials sent successfully!");
                  })
                  .catch(() => {
                    setLoginCredentialsPromptOpen(true);
                    setSendLoginCredentialsError("Error");
                  });
              }
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "main content",
                section: "send login credentials",
                text: sendLoginCredentialsError ? "Ok" : "Send",
              });
            }}
            handleCancelClick={() => {
              setLoginCredentialsPromptOpen(false);
              generateGtmEvent({
                event: "link",
                action: "click",
                name: "onclick",
                type: "internal link",
                region: "main content",
                section: "send login credentials",
                text: "cancel",
              });
            }}
            okButtonLabel={sendLoginCredentialsError ? "Ok" : "Send"}
            popupTitle={`Send login credentials`}
            cancelButtonLabel={!sendLoginCredentialsError ? "Cancel" : undefined}>
            <Stack direction={"column"} spacing={4}>
              {sendLoginCredentialsError && (
                <Typography variant="subtitle2">
                  {selectedUser.full_name} has already activated their account
                  successfully.
                </Typography>
              )}
              <Typography variant="body1">
                {sendLoginCredentialsError
                  ? `Please have them reset their password from the login page if they are having trouble logging in, or contact support if you need additional assistance.`
                  : `Confirm you would like to send ${selectedUser.full_name} a welcome email with a temporary password.`}
              </Typography>
            </Stack>
          </PopUpDialog>
        )}
      </div>
    </>
  );
};
